import { useEffect, useState } from 'react';
import { assetService } from '@/services/asset-service';

type VideoPreviewProps = {
  /** A local file to be previewed - this would be used for previewing files before upload */
  localFile?: File;
  /** The UUID of the VIDEO asset to be previewed. */
  assetId?: string;
  /** Optional alt text for the video */
  alt?: string;
};

export const VideoPreview = ({ 
  localFile, 
  assetId, 
  alt = "Asset preview"
}: VideoPreviewProps) => {
  const [videoSrc, setVideoSrc] = useState<string | null>(null);

  useEffect(() => {
    const fetchVideoAsset = async () => {
      if (assetId) {
        try {
          // Get the file URI from the asset
          const uri = await assetService.getAssetFileUri(assetId);
          setVideoSrc(uri);
        } catch (error) {
          console.error('Error fetching video asset:', error);
          setVideoSrc(null);
        }
      }
    };

    if (localFile) {
      // Create a temporary URL for the local file
      const url = URL.createObjectURL(localFile);
      setVideoSrc(url);

      // Clean up the URL object when component unmounts or file changes
      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (assetId) {
      fetchVideoAsset();
    }
  }, [localFile, assetId]);

  if (!videoSrc) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-muted">
        <div className="text-center">
          <p className="text-sm text-muted-foreground">
            {assetId ? "Video not available" : "Loading video..."}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex items-center justify-center p-2">
      <video 
        src={videoSrc} 
        controls
        className="max-w-full max-h-full object-contain"
        aria-label={alt}
      />
    </div>
  );
};